import { useState } from "react";
import { AtSign, Copy, ExternalLink, Globe, Phone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import SvgIcon from "components/base/SvgIcon";
import {
  formatSocialLinkText,
  getIconForSocialType,
  getSocialProfileUrl,
} from "components/UserCardList/helpers/userCard";
import { cn } from "utils";

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
};

const LABEL_CLASS =
  "text-sm font-normal text-gray-500 truncate flex-1 min-w-0 no-underline hover:text-gray-600 hover:no-underline";

const ContactRow = ({ icon, label, href, onCopy, external }) => (
  <div className="flex items-center gap-3 py-2 min-w-0">
    <div className="text-gray-500 shrink-0 w-5 flex justify-center">{icon}</div>
    {href ? (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={LABEL_CLASS}
        onClick={(e) => e.stopPropagation()}
      >
        {label}
      </a>
    ) : (
      <span className={LABEL_CLASS}>{label}</span>
    )}
    {onCopy ? (
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 shrink-0 p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded"
        title="Copy"
        onClick={(e) => {
          e.stopPropagation();
          onCopy();
        }}
      >
        <Copy size={16} strokeWidth={1.5} />
      </button>
    ) : external ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-gray-400 hover:text-gray-600 shrink-0 p-0.5 no-underline outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded"
        onClick={(e) => e.stopPropagation()}
        title={`Open ${label}`}
      >
        <ExternalLink size={16} strokeWidth={1.5} />
      </a>
    ) : null}
  </div>
);

const ContactPopover = ({ hit }) => {
  const [copiedField, setCopiedField] = useState(null);

  const whatsAppAllowed = hit?.phone?.whatsAppAllowed && hit?.phone?.number;
  const hasSocialLinks = hit.socialLinks?.length > 0;

  const handleCopy = async (value, field) => {
    await copyToClipboard(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const hasContact =
    hit.email || hit?.phone?.number || hasSocialLinks || whatsAppAllowed;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "p-1.5 text-gray-900 hover:text-black hover:bg-gray-100 rounded transition-colors",
            !hasContact && "opacity-40"
          )}
          title="Contact"
          onClick={(e) => e.stopPropagation()}
          disabled={!hasContact}
        >
          <Globe size={20} strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      {hasContact && (
        <PopoverContent
          align="end"
          className="w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg text-gray-500"
          onClick={(e) => e.stopPropagation()}
        >
          {hit.email && (
            <ContactRow
              icon={<AtSign size={18} />}
              label={hit.email}
              href={`mailto:${hit.email}`}
              onCopy={() => handleCopy(hit.email, "email")}
            />
          )}
          {hit?.phone?.number && (
            <ContactRow
              icon={<Phone size={18} />}
              label={hit.phone.number}
              href={`tel:${hit.phone.number}`}
              onCopy={() => handleCopy(hit.phone.number, "phone")}
            />
          )}
          {hit.socialLinks?.map((socialLink) => (
            <ContactRow
              key={`${socialLink.type}-${socialLink.value}`}
              icon={
                <SvgIcon
                  type={getIconForSocialType(socialLink.type)}
                  className="!w-[18px] !h-[18px] !fill-gray-500"
                />
              }
              label={formatSocialLinkText(socialLink.type)}
              href={getSocialProfileUrl(socialLink.type, socialLink.value)}
              external
            />
          ))}
          {whatsAppAllowed && (
            <ContactRow
              icon={
                <SvgIcon
                  type="whatsapp"
                  className="!w-[18px] !h-[18px] !fill-gray-500"
                />
              }
              label="WhatsApp"
              href={`https://wa.me/${hit.phone.number.replace(/\D/g, "")}`}
              external
            />
          )}
          {copiedField && (
            <p className="text-xs text-green-600 mt-1 px-1">Copied!</p>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
};

export default ContactPopover;
